import { AfterViewInit, Component, ElementRef, Input, ViewChild, forwardRef, ChangeDetectionStrategy, OnDestroy } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { RECAPTCHA_SITEKEY } from "../../constants";

declare var grecaptcha: any;

@Component({
    selector: 'ev-captcha',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => EvCaptchaComponent),
        }
    ],
    template: `<div #container></div>`,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class EvCaptchaComponent implements AfterViewInit, OnDestroy {
    @Input() theme: 'dark' | 'light' = 'light'
    @Input() siteKey: string
    @Input() timeout: number = 100

    __valid = false


    get valid() {
        return this.__valid
    }


    @ViewChild('container', { static: true }) container: ElementRef


    statusChanged = new BehaviorSubject<boolean>(false)

    private destroyed = false
    private widgetId: number | null = null

    ngAfterViewInit(): void {
        void this.render()
    }

    ngOnDestroy(): void {
        this.destroyed = true
    }

    async render() {
        try {
            await this.wait(this.timeout)
            const captcha = await this.waitForRecaptcha()

            if (this.destroyed || this.widgetId !== null) {
                return
            }

            this.widgetId = captcha.render(this.container.nativeElement, {
                sitekey: this.siteKey || RECAPTCHA_SITEKEY,
                theme: this.theme,
                callback: () => {
                    this.updateStatus(true)
                },
                'expired-callback': () => this.updateStatus(false),
                'error-callback': () => this.updateStatus(false)
            })
        } catch (error) {
            // Lo script di Google e' esterno: non deve impedire al form di caricarsi.
            // Lasciamo il CAPTCHA non valido e rendiamo l'errore rintracciabile in console.
            console.error('Impossibile inizializzare reCAPTCHA.', error)
        }
    }


    private updateStatus(valid: boolean) {
        this.__valid = valid
        this.statusChanged.next(valid)
    }


    private wait(milliseconds: number): Promise<void> {
        return new Promise(resolve => window.setTimeout(resolve, milliseconds))
    }


    private waitForRecaptcha(): Promise<any> {
        if (window['grecaptcha']?.render) {
            return Promise.resolve(window['grecaptcha'])
        }

        return new Promise((resolve, reject) => {
            let script = document.querySelector<HTMLScriptElement>('script[src*="google.com/recaptcha/api.js"]')

            if (!script) {
                script = document.createElement('script')
                script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
                script.async = true
                script.defer = true
                document.head.appendChild(script)
            }

            const pollingId = window.setInterval(() => {
                if (window['grecaptcha']?.render) {
                    cleanup()
                    resolve(window['grecaptcha'])
                }
            }, 50)

            const timeoutId = window.setTimeout(() => {
                cleanup()
                reject(new Error('Lo script reCAPTCHA non e\' stato caricato entro 10 secondi.'))
            }, 10000)

            const onError = () => {
                cleanup()
                reject(new Error('Impossibile caricare lo script reCAPTCHA.'))
            }

            const cleanup = () => {
                window.clearInterval(pollingId)
                window.clearTimeout(timeoutId)
                script.removeEventListener('error', onError)
            }

            script.addEventListener('error', onError, { once: true })
        })
    }

}
